// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import * as tracker from 'presentations/views/helpers/analytics';
import { ViewerItem } from 'presentations/api/schema/user';
import { usePusher } from './pusher';
import { useContext, useEffect } from 'react';
import {
  MeetingWidgetContext,
  setMessage,
  setViewer,
  setIsShowIcon,
  setShowingMenu,
  setUid,
  setUrl,
} from 'presentations/reducers/MeetingWidget';
import { getDateTime } from 'infras/datetime';
import {
  DashboardPortalContext,
  setInfoMessage,
} from 'presentations/reducers/DashboardPortal';

interface Props {
  uid: string | null;
  url: string | null;
  viewer: ViewerItem | null;
}

let scheduledAtTimer: number | null = null;

export function useMeetingWidget(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(MeetingWidgetContext);
  const dashboardNavigationContext = useContext(DashboardPortalContext);

  const pusher = usePusher({
    state,
    dispatch,
  });

  useEffect(() => {
    if (state.callUserId) return;
    dispatch(setUid(props.uid));
    dispatch(setUrl(props.url));
  }, [props.uid]);

  useEffect(() => {
    if (state.callUserId) return;
    dispatch(setUid(props.uid));
    dispatch(setUrl(props.url));
  }, [state.callUserId]);

  useEffect(() => {
    dispatch(setViewer(props.viewer));
  }, [props.viewer]);

  useEffect(() => {
    if (state.callUserId && state.liveViewer) {
      pusher.actions.setLocalCallUserId(state.callUserId);
    }
  }, [state.callUserId]);

  useEffect(() => {
    const cleanUp = () => {
      if (scheduledAtTimer) {
        clearInterval(scheduledAtTimer);
        scheduledAtTimer = null;
      }
    };
    if (!state.scheduledAt) return cleanUp;
    const scheduledAt = getDateTime(state.scheduledAt).toMillis();
    const now = getDateTime().toMillis();
    let offsets = scheduledAt - now;
    // https://developer.mozilla.org/ja/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#maximum_delay_value
    const setTimeoutLimit = 60 * 60 * 24 * 24 * 1000;
    if (offsets > 0) {
      if (offsets > setTimeoutLimit) {
        offsets = setTimeoutLimit;
      }
      scheduledAtTimer = setTimeout(() => {
        dashboardNavigationContext.dispatch(
          setInfoMessage(t('Meeting start now')),
        );
      }, offsets);
    }
    return cleanUp;
  }, [state.scheduledAt]);

  function onClickCloseMenu() {
    dispatch(setShowingMenu(null));
  }

  const onCloseMessage = async () => {
    dispatch(setMessage(null));
  };

  function onClickShowIcon() {
    dispatch(setIsShowIcon(!state.isShowIcon));
    tracker.setEvent(`meeting icon ${state.isShowIcon ? 'hide' : 'show'}`);
  }

  return {
    state: {
      ...state,
      connectUserCount: state.channel ? state.channel.members.count : 0,
    },
    actions: {
      onClickShowIcon,
      setCurrentPage: pusher.actions.setCurrentPage,
      onCloseMessage,
      onClickCloseMenu,
    },
  };
}
